import { TOKEN_VARIANT_CONFIGS, SINGLE_VARIANT } from '../types';
import { deriveColorVariants, getBestTextColor, getDisabledTextColor, getSubtleTextColor, hexToHsl, hslToHex } from './color';

// Colors
export function derivePrimaryColorVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  const textColor = getBestTextColor(baseColor);
  const disabledTextColor = getDisabledTextColor(baseColor);
  return {
    'fill-primary-default': variants.default,
    'fill-primary-hover': variants.hover,
    'fill-primary-pressed': variants.pressed,
    'fill-primary-disabled': variants.disabled,
    'border-primary-default': baseColor,
    'text-primary-default': textColor,
    'text-inverse-default': textColor,
    'text-primary-disabled': disabledTextColor,
    // used for PanelHeaders which expects two colors for a
    // gradient background-image. These colors will a subtle gradient
    // that I think is appropriate until we get more customer feedback.
    'fill-transitional-modal-gradient-color-1': variants.default,
    'fill-transitional-modal-gradient-color-2': variants.pressed
  };
}
export function deriveSecondaryColorVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  const textColor = getBestTextColor(baseColor);
  return {
    'fill-secondary-default': variants.default,
    'fill-secondary-hover': variants.hover,
    'fill-secondary-pressed': variants.pressed,
    'fill-secondary-disabled': variants.disabled,
    'border-secondary-default': baseColor,
    'text-secondary-default': textColor
  };
}
export function deriveTertiaryColorVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  const textColor = getBestTextColor(baseColor);
  return {
    'fill-tertiary-default': variants.default,
    'fill-transitional-tertiary-default': variants.default,
    'fill-transitional-tertiary-hover': variants.hover,
    'fill-transitional-tertiary-pressed': variants.pressed,
    'fill-tertiary-hover': variants.hover,
    'fill-tertiary-pressed': variants.pressed,
    'fill-tertiary-disabled': variants.disabled,
    'border-tertiary-default': baseColor,
    'text-inverse-default': textColor,
    // The token below is used on tertiary-light versions of buttons
    'text-tertiary-default': textColor
  };
}
export function deriveSurfaceTokensFromStyle(style) {
  const baseColor = style === 'light' ? '#FFFFFF' : '#121212';
  const variants = deriveColorVariants(baseColor);
  const textColor = getBestTextColor(baseColor);
  const subtleText = getSubtleTextColor(baseColor);
  const surfaceHsl = hexToHsl(baseColor);
  const isDark = surfaceHsl.l < 50;
  const TABLE_HEADER_LIGHTNESS_OFFSET = 5;

  // For dark surfaces, borders should be lighter
  // for light surfaces, borders should be darker
  const borderDefault = hslToHex(Object.assign({}, surfaceHsl, {
    l: isDark ? Math.max(Math.min(surfaceHsl.l + 15, 100), 35) : Math.max(surfaceHsl.l - 15, 0),
    s: Math.min(surfaceHsl.s, 10)
  }));
  const borderSubtle = hslToHex(Object.assign({}, surfaceHsl, {
    l: isDark ? Math.max(Math.min(surfaceHsl.l + 8, 100), 20) : Math.max(surfaceHsl.l - 8, 0),
    s: Math.min(surfaceHsl.s, 5)
  }));

  // Raised surface: lighter in dark mode, slightly darker in light mode
  const RAISED_LIGHTNESS_OFFSET = 3;
  const RAISED_DARK_OFFSET = 5;
  const raisedSurface = hslToHex(Object.assign({}, surfaceHsl, {
    l: isDark ? Math.min(surfaceHsl.l + RAISED_DARK_OFFSET, 100) : Math.max(surfaceHsl.l - RAISED_LIGHTNESS_OFFSET, 0)
  }));

  // Recessed surface should be darker than default for both themes
  const RECESSED_LIGHTNESS_OFFSET = 6;
  const recessedSurface = hslToHex(Object.assign({}, surfaceHsl, {
    l: Math.max(surfaceHsl.l - RECESSED_LIGHTNESS_OFFSET, 0)
  }));
  const recessedVariants = deriveColorVariants(recessedSurface);

  // Field background should adapt to surface color
  const fieldBg = isDark ? raisedSurface : variants.subtle;

  // Disabled colors adapt to surface
  const disabledBorder = isDark ? '#4a4a4a' : '#d1d1d1';
  const disabledFill = isDark ? '#2a2a2a' : '#f5f5f5';
  // Disabled text uses WCAG AA compliant grays (4.5:1+ contrast ratio)
  const disabledText = isDark ? '#8a8a8a' : '#767676';

  // Calculate table header with slight lightness offset to distinguish from rows
  const tableHeaderDefault = hslToHex(Object.assign({}, surfaceHsl, {
    l: isDark ? Math.min(surfaceHsl.l + TABLE_HEADER_LIGHTNESS_OFFSET, 100) : Math.max(surfaceHsl.l - TABLE_HEADER_LIGHTNESS_OFFSET, 0)
  }));

  // Derive hover/pressed from the adjusted header color
  const tableHeaderVariants = deriveColorVariants(tableHeaderDefault);
  const tableHeaderHover = tableHeaderVariants.hover;
  const tableHeaderPressed = tableHeaderVariants.pressed;

  // Accent neutral variants - provides contrast against surface
  const accentNeutralDefault = isDark ? '#E0E0E0' : '#333333';
  const accentNeutralVariants = deriveColorVariants(accentNeutralDefault);
  // Subtle variants blend with the surface (opposite of default)
  const accentNeutralSubtle = isDark ? '#1a1a1a' : '#E6E6E6';
  const accentNeutralSubtleAlt = isDark ? '#1a1a1a' : '#F0F0F0';
  return {
    // Surface fills
    'fill-surface-default': variants.default,
    'fill-surface-default-hover': variants.hover,
    'fill-surface-default-pressed': variants.pressed,
    'fill-surface-raised': raisedSurface,
    'fill-surface-recessed': recessedSurface,
    'fill-surface-recessed-hover': recessedVariants.hover,
    'fill-surface-recessed-pressed': recessedVariants.pressed,
    // Border tokens
    'border-core-default': borderDefault,
    'border-core-subtle': borderSubtle,
    'border-core-disabled': disabledBorder,
    'border-core-hover': variants.hover,
    'border-transitional-core-subtle': isDark ? borderSubtle : borderDefault,
    // Text tokens
    'text-core-default': textColor,
    'text-core-subtle': subtleText,
    'text-core-on-fill-default': isDark ? '#1F1F1F' : '#FFFFFF',
    'text-core-disabled': disabledText,
    'text-interactive-default-disabled': disabledText,
    // Field tokens
    'fill-field-default': fieldBg,
    'fill-field-default-alt': variants.hover,
    'fill-field-disabled': disabledFill,
    'fill-field-hover': variants.hover,
    // Table colors - may be adapted based on surface contrast
    'specialty-table-header-default': tableHeaderDefault,
    'specialty-table-header-hover': tableHeaderHover,
    'specialty-table-header-pressed': tableHeaderPressed,
    // icon interactive colors
    'icon-interactive-default': textColor,
    'icon-interactive-disabled': subtleText,
    'icon-interactive-hover': tableHeaderHover,
    'icon-interactive-pressed': variants.pressed,
    'icon-interactive-on-fill-default': variants.default,
    'icon-interactive-on-fill-hover': variants.hover,
    'icon-interactive-on-fill-pressed': variants.pressed,
    // icon core colors (used by Table sort arrows)
    'icon-core-default': textColor,
    'icon-core-disabled': disabledText,
    'icon-core-subtle': subtleText,
    'icon-core-on-fill-default': isDark ? '#1F1F1F' : '#FFFFFF',
    // neutral fill for tooltips and similar components
    'fill-accent-neutral-default': accentNeutralDefault,
    'fill-accent-neutral-hover': accentNeutralVariants.hover,
    'fill-accent-neutral-pressed': accentNeutralVariants.pressed,
    'fill-accent-neutral-subtle': accentNeutralSubtle,
    'fill-accent-neutral-subtle-alt': accentNeutralSubtleAlt
  };
}
export function deriveTextInteractiveDefaultColorVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  return {
    'text-interactive-default': variants.default,
    'text-interactive-default-hover': variants.hover,
    'text-interactive-default-pressed': variants.pressed,
    'specialty-text-interactive-alt-default': variants.default,
    'specialty-text-interactive-alt-hover': variants.hover,
    'specialty-text-interactive-alt-pressed': variants.pressed,
    'border-interactive-default': variants.default
  };
}
export function deriveColorFillCautionDefaultVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  return {
    // status tags
    'fill-caution-default': variants.default,
    'fill-caution-hover': variants.hover,
    'fill-caution-pressed': variants.pressed,
    'fill-accent-yellow-subtle-alt': variants.subtle,
    'fill-caution-subtle': variants.subtle,
    // tag components
    'border-caution-default': variants.default
  };
}
export function deriveColorFillAccentGreenDefaultVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  return {
    // status tags
    'fill-accent-green-default': variants.default,
    'fill-accent-green-subtle-alt': variants.subtle,
    'fill-accent-green-pressed': variants.pressed,
    'fill-accent-green-hover': variants.hover,
    // tag components
    'border-accent-green-default': variants.default,
    'border-positive-default': variants.default,
    'fill-positive-subtle': variants.subtle
  };
}
export function deriveColorFillAccentRedDefaultVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  return {
    // status tags
    'fill-accent-red-default': variants.default,
    'fill-accent-red-subtle-alt': variants.subtle,
    'fill-accent-red-pressed': variants.pressed,
    'fill-accent-red-hover': variants.hover,
    'fill-alert-subtle': variants.subtle,
    'border-alert-default': variants.default
  };
}
export function deriveColorFillInfoDefaultVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  return {
    'fill-info-default': variants.default,
    'fill-info-subtle': variants.subtle,
    'fill-info-hover': variants.hover,
    'fill-info-pressed': variants.pressed,
    'border-info-default': variants.default,
    'border-transitional-interactive-subtle': variants.subtle,
    'fill-accent-blue-default': variants.default
  };
}
const tokenConfigMap = new Map();
for (const config of TOKEN_VARIANT_CONFIGS) {
  for (const prefix of config.prefixes) {
    tokenConfigMap.set(prefix, {
      variants: config.variants,
      derivedTokens: config.derivedTokens
    });
  }
}
function getConfigForTokenPrefix(tokenPrefix) {
  const exactMatch = tokenConfigMap.get(tokenPrefix);
  if (exactMatch) {
    return exactMatch;
  }
  return {
    variants: SINGLE_VARIANT
  };
}

// Derives all variant tokens for a given base color and target token.
export function deriveGenericColorVariants(baseColor, targetToken) {
  var _config$derivedTokens, _config$derivedTokens2, _config$derivedTokens3;
  const tokenPrefix = targetToken.replace(/-(default|subtle|hover|pressed)$/, '');
  const config = getConfigForTokenPrefix(tokenPrefix);
  if (config.variants.length === 0) {
    return {
      [targetToken]: baseColor
    };
  }
  const colorVariants = deriveColorVariants(baseColor);
  const result = {};
  for (const variant of config.variants) {
    const variantKey = variant;
    if (colorVariants[variantKey]) {
      result[`${tokenPrefix}-${variant}`] = colorVariants[variantKey];
    }
  }
  if ((_config$derivedTokens = config.derivedTokens) !== null && _config$derivedTokens !== void 0 && _config$derivedTokens.text) {
    const textColor = getBestTextColor(baseColor);
    result[`${config.derivedTokens.text}-default`] = textColor;
  }
  if ((_config$derivedTokens2 = config.derivedTokens) !== null && _config$derivedTokens2 !== void 0 && _config$derivedTokens2.border) {
    result[`${config.derivedTokens.border}-default`] = baseColor;
  }
  if ((_config$derivedTokens3 = config.derivedTokens) !== null && _config$derivedTokens3 !== void 0 && _config$derivedTokens3.icon) {
    const iconColor = getBestTextColor(baseColor);
    result[`${config.derivedTokens.icon}-default`] = iconColor;
  }
  return result;
}
export const COLOR_OVERRIDE_CONFIGS = {
  'fill-primary-default': {
    brandField: 'primaryColor',
    themeSettingField: 'trellisColorFillPrimaryDefault',
    deriveVariants: derivePrimaryColorVariants
  },
  'fill-secondary-default': {
    brandField: 'secondaryColor',
    themeSettingField: 'trellisColorFillSecondaryDefault',
    deriveVariants: deriveSecondaryColorVariants
  },
  'fill-tertiary-default': {
    brandField: 'accentColor1',
    themeSettingField: 'trellisColorFillTertiaryDefault',
    deriveVariants: deriveTertiaryColorVariants
  },
  'text-interactive-default': {
    brandField: null,
    themeSettingField: 'trellisColorTextInteractiveDefault',
    deriveVariants: deriveTextInteractiveDefaultColorVariants
  },
  // The below configs are not customizable via CMS theme fields
  // but necessary to allow us to derive the necessary variants for
  // the default token values we set for the base color token keys
  // (e.g. fill-caution-default, fill-accent-green-default, etc)
  'fill-caution-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillCautionDefaultVariants
  },
  'fill-accent-green-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillAccentGreenDefaultVariants
  },
  'fill-accent-red-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillAccentRedDefaultVariants
  },
  'fill-info-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillInfoDefaultVariants
  }
  // @TODO add default status tag colors: icon-core-disabled, text-core-subtle
  // @TODO add alert components
};
export const SURFACE_STYLE_OVERRIDE_CONFIG = {
  themeSettingField: 'trellisColorFillSurfaceDefault',
  deriveVariants: deriveSurfaceTokensFromStyle
};

// Borders
export function deriveBorderRadiusVariants(borderRadiusChoice) {
  // This is the default so we will fallback to the current borderRadius values
  // within Trelils.
  if (borderRadiusChoice === 'rounded') {
    return {
      '0': '0px',
      '100': '3px',
      '200': '4px',
      '300': '8px',
      '400': '16px',
      'transitional-container': '0px',
      'transitional-container-alt': '3px',
      'transitional-component': '0px',
      'transitional-floating': '3px',
      'transitional-full-0': '0px',
      'transitional-full-3': '3px',
      'transitional-full-8': '8px'
    };
  } else if (borderRadiusChoice === 'pill') {
    return {
      // does this default make sense to use this 0 as the fallback?
      // necessary to allow consuming teams to set a different border radius
      // for tables when the rest of border radius are in pill mode
      '0': '16px',
      '100': '999999px',
      '200': '999999px',
      '300': '999999px',
      '400': '999999px',
      'transitional-container': '999999px',
      'transitional-container-alt': '999999px',
      'transitional-component': '999999px',
      'transitional-floating': '999999px',
      'transitional-full-0': '999999px',
      'transitional-full-3': '999999px',
      'transitional-full-8': '999999px'
    };
  } else {
    // otherwise its square, so we need to clear out all border-radius values
    return {
      '0': '0px',
      '100': '0px',
      '200': '0px',
      '300': '0px',
      '400': '0px',
      'transitional-container': '0px',
      'transitional-container-alt': '0px',
      'transitional-component': '0px',
      'transitional-floating': '0px',
      'transitional-full-0': '0px',
      'transitional-full-3': '0px',
      'transitional-full-8': '0px'
    };
  }
}
export const BORDER_RADIUS_OVERRIDE_CONFIGS = [{
  themeSettingField: 'trellisBorderRadius',
  deriveVariants: deriveBorderRadiusVariants
}];
function buildFontFamilyString(fontName) {
  return `${fontName}, sans-serif`;
}

// Fonts
const BODY_TYPOGRAPHY_TOKENS = ['body-100', 'body-125', 'body-200', 'body-300', 'body-400', 'body-500', 'body-600', 'body-700', 'detail-100', 'detail-200', 'link-25', 'link-100', 'link-200'];
const HEADING_TYPOGRAPHY_TOKENS = ['heading-25', 'heading-50', 'heading-100', 'heading-200', 'heading-300', 'heading-400', 'heading-500', 'heading-600', 'heading-700', 'heading-800', 'heading-900', 'heading-1000', 'display-100', 'display-200', 'display-300', 'display-400', 'display-500'];
function deriveFontVariants(fontFamily, tokens) {
  const typographyOverrides = {};
  const fullFontFamily = buildFontFamilyString(fontFamily);
  tokens.forEach(token => {
    typographyOverrides[token] = {
      fontFamily: fullFontFamily
    };
  });
  return typographyOverrides;
}
export function deriveBodyFontVariants(fontFamily) {
  return deriveFontVariants(fontFamily, BODY_TYPOGRAPHY_TOKENS);
}
export function deriveHeadingFontVariants(fontFamily) {
  return deriveFontVariants(fontFamily, HEADING_TYPOGRAPHY_TOKENS);
}
export const FONT_OVERRIDE_CONFIGS = {
  body: {
    brandField: 'bodyFont',
    themeSettingField: 'bodyFont',
    deriveVariants: deriveBodyFontVariants
  },
  heading: {
    brandField: 'primaryFont',
    themeSettingField: 'primaryFont',
    deriveVariants: deriveHeadingFontVariants
  }
};